import { MyError } from '../extends';

export default class Queue {
  private tasks: Array<() => Promise<any>> = [];

  push<T extends (...args: A) => ResolveValue<any>, A extends any[] = any[]>(
    task: T,
    ...args: A
  ): Promise<ReturnType<T>> {
    return new Promise((resolve, reject) => {
      this.tasks.push(async () => {
        try {
          resolve(await task(...args));
        } catch (err) {
          reject(err);
        }
      });
      if (this.tasks.length === 1) {
        this.processTasks();
      }
    });
  }

  private async processTasks(): Promise<void> {
    while (this.tasks.length > 0) {
      await this.tasks[0]();
      this.tasks.shift();
    }
  }
}

export function useQueue() {
  return useMemo(() => new Queue(), []);
}

export class PromiseTimeoutError extends MyError {}

export function promiseTimeout<T>(handler: () => ResolveValue<T>, timeout = 30000) {
  let timerId: NodeJS.Timeout;
  return Promise.race([
    handler(),
    new Promise<never>(
      (_, reject) =>
        (timerId = setTimeout(
          () => reject(new PromiseTimeoutError('Timeout')),
          timeout
        ))
    ),
  ]).finally(() => {
    clearTimeout(timerId);
  });
}

export interface RetryOptions {
  maxCount?: number;
  maxTime?: number;
  interval?: number;
  timeout?: number;
  isRetry?: (err: any) => ResolveValue<boolean>;
  catchError?: (err: any) => ResolveValue<any>;
}

export async function retry<T extends (byRetry: boolean) => ResolveValue<any>>(
  cb: T,
  options?: RetryOptions
): Promise<Awaited<ReturnType<T>>> {
  const {
    maxCount = Infinity,
    maxTime = 60 * 1000,
    interval = 1000,
    timeout = 30 * 1000,
    isRetry,
    catchError = (err: unknown) => {
      throw err;
    },
  } = options || {};

  const startTime = Date.now();
  const timeout_ = Math.min(timeout, 1147483647);

  let maxCount_ = maxCount;
  let byRetry = false;

  while (true) {
    try {
      return await promiseTimeout(() => cb(byRetry), timeout_);
    } catch (err) {
      try {
        return await catchError(err);
      } catch (err) {
        if (
          maxCount_ > 0 &&
          maxTime > Date.now() - startTime &&
          (!isRetry || (await isRetry(err)))
        ) {
          if (interval > 0) {
            await new Promise((resolve) => setTimeout(resolve, interval));
          }

          maxCount_--;
          byRetry = true;
        } else {
          throw err;
        }
      }
    }
  }
}
